// Season header: large title, record / runs for / runs against, last-5 chips,
// snapshot provenance, Smart Scout + import entry points, and the quick-jump
// section chips (Story · Players · Pitching · Field Time).

import React from 'react';
import { Sparkles } from 'lucide-react';
import { GS } from '../../lib/designTokens';
import { Card } from './ui';
import type { SeasonData } from './useSeasonStats';

export const SeasonHeader = ({ data, elite, isHistoryMode, viewSeason, playersPitch, onOpenScout }: {
  data: SeasonData;
  elite: boolean;
  isHistoryMode: boolean;
  viewSeason?: { season: string; year: number } | null;
  playersPitch: boolean;
  onOpenScout: () => void;
}) => {
  const r = data.record;
  const hasResults = r.w + r.l + r.t > 0;

  const jump = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const chips = [
    { id: 'sec-story', label: 'Story' },
    { id: 'sec-players', label: 'Players' },
    ...(playersPitch ? [{ id: 'sec-pitching', label: 'Pitching' }] : []),
    { id: 'sec-fairness', label: 'Field Time' },
  ];

  return (
    <div>
      <div className="flex items-start justify-between gap-3 px-1 pt-2">
        <div>
          <h1 className="text-3xl font-black italic" style={{ color: GS.ink, letterSpacing: '-0.01em' }}>
            {isHistoryMode && viewSeason ? `${viewSeason.season} ${viewSeason.year}` : 'Season'}
          </h1>
          {data.snapshotImportedAt ? (
            <p className="text-[10px] font-bold mt-1" style={{ color: GS.greenInk }}>
              GameChanger import · updated {new Date(data.snapshotImportedAt).toLocaleDateString()}
            </p>
          ) : (
            <p className="text-[10px] font-bold mt-1" style={{ color: GS.ink3 }}>
              {data.sortedGames.length} {data.sortedGames.length === 1 ? 'game' : 'games'} logged
            </p>
          )}
        </div>
        <div className="flex gap-2 shrink-0">
          {!isHistoryMode && elite && (
            <button
              onClick={onOpenScout}
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-[10px] font-black uppercase text-ink"
              style={{ background: `linear-gradient(150deg, ${GS.gold}, ${GS.gold2})`, letterSpacing: '0.08em' }}
            >
              <Sparkles className="w-3 h-3" />
              Scout
            </button>
          )}
        </div>
      </div>

      {hasResults && (
        <Card className="mt-3">
          <div className="grid grid-cols-3 py-4">
            {[
              { v: `${r.w}–${r.l}${r.t ? `–${r.t}` : ''}`, l: 'Record' },
              { v: String(r.runsFor), l: 'Runs For' },
              { v: String(r.runsAgainst), l: 'Runs Against' },
            ].map((m, i) => (
              <div key={m.l} className="text-center" style={{ borderLeft: i > 0 ? `1px solid ${GS.line}` : undefined }}>
                <div className="text-2xl font-black tabular-nums" style={{ color: GS.ink }}>{m.v}</div>
                <div className="text-[9px] font-extrabold uppercase mt-1" style={{ color: GS.ink3, letterSpacing: '0.1em' }}>{m.l}</div>
              </div>
            ))}
          </div>
          {r.last5.length > 0 && (
            <div className="flex items-center justify-center gap-1.5 py-2.5" style={{ borderTop: `1px solid ${GS.line}` }}>
              <span className="text-[9px] font-extrabold uppercase mr-1" style={{ color: GS.ink3, letterSpacing: '0.1em' }}>Last {r.last5.length}</span>
              {r.last5.map((o, i) => (
                <span
                  key={i}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-black"
                  style={o === 'W'
                    ? { background: GS.greenSoft, color: GS.greenInk, border: `1px solid ${GS.green}` }
                    : o === 'L'
                    ? { background: 'rgba(229,72,77,0.08)', color: GS.red, border: '1px solid rgba(229,72,77,0.35)' }
                    : { background: GS.panel2, color: GS.ink2, border: `1px solid ${GS.line}` }}
                >
                  {o}
                </span>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Quick-jump chips */}
      <div className="flex gap-2 mt-3 overflow-x-auto pb-0.5" style={{ scrollbarWidth: 'none' }}>
        {chips.map(c => (
          <button
            key={c.id}
            onClick={() => jump(c.id)}
            className="shrink-0 px-3.5 py-1.5 rounded-full text-[10px] font-black uppercase"
            style={{ background: GS.panel, color: GS.ink2, border: `1px solid ${GS.line}`, letterSpacing: '0.08em' }}
          >
            {c.label}
          </button>
        ))}
      </div>
    </div>
  );
};
